import { useState, useEffect } from 'react';
import { API_URL } from './constants';
import { authHeaders, mapStubToLine } from './helpers';
import type { Run, Stub, Line, PayrollSettings, Schedule, Comparison } from './types';

export interface PayRunData {
  run: Run | null;
  lines: Line[];
  payrollSettings: PayrollSettings | null;
  priorRuns: Run[];
  comparison: Comparison | null;
  schedule: Schedule | null;
  loading: boolean;
  error: string | null;
  isFinalized: boolean;
  setLines: (next: Line[]) => void;
}

export function usePayRun(payRunId: string | undefined): PayRunData {
  const [run, setRun] = useState<Run | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [payrollSettings, setPayrollSettings] = useState<PayrollSettings | null>(null);
  const [priorRuns, setPriorRuns] = useState<Run[]>([]);
  const [comparison, setComparison] = useState<Comparison | null>(null);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFinalized, setIsFinalized] = useState<boolean>(false);

  useEffect(() => {
    if (!payRunId) return;
    let cancelled = false;

    const fetchAll = async (): Promise<void> => {
      const headers = authHeaders();
      try {
        const [runRes, stubsRes, settingsRes, runsRes, comparisonRes] = await Promise.all([
          fetch(API_URL + '/api/v1/payroll/runs/' + payRunId, { headers })
            .then((r) => (r.ok ? r.json() : null)).catch(() => null),
          fetch(API_URL + '/api/v1/payroll/runs/' + payRunId + '/stubs', { headers })
            .then((r) => (r.ok ? r.json() : [])).catch(() => []),
          fetch(API_URL + '/api/v1/payroll/settings', { headers })
            .then((r) => (r.ok ? r.json() : null)).catch(() => null),
          fetch(API_URL + '/api/v1/payroll/runs', { headers })
            .then((r) => (r.ok ? r.json() : [])).catch(() => []),
          fetch(API_URL + '/api/v1/payroll/runs/' + payRunId + '/comparison', { headers })
            .then((r) => (r.ok ? r.json() : null)).catch(() => null),
        ]);
        if (cancelled) return;

        if (comparisonRes) setComparison(comparisonRes);

        if (!runRes) {
          setError('Could not load pay run.');
          setLoading(false);
          return;
        }

        setRun(runRes);
        setPayrollSettings(settingsRes);

        if (runRes.pay_schedule_id) {
          fetch(API_URL + '/api/v1/payroll/schedules/' + runRes.pay_schedule_id, { headers })
            .then((r) => (r.ok ? r.json() : null))
            .then((sched: Schedule | null) => { if (sched && !cancelled) setSchedule(sched); })
            .catch(() => undefined);
        }

        if (runRes.status === 'finalized' || runRes.status === 'voided') {
          setIsFinalized(true);
          setLoading(false);
          return;
        }

        const priorRunsData: Run[] = Array.isArray(runsRes)
          ? runsRes.filter((r: Run) => r.status === 'finalized' && String(r.id) !== String(payRunId))
          : [];
        setPriorRuns(priorRunsData);

        const stubs: Stub[] = Array.isArray(stubsRes) ? stubsRes : [];
        setLines(stubs.map(mapStubToLine));
        setLoading(false);
      } catch (e: any) {
        if (!cancelled) {
          setError(String(e?.message || e));
          setLoading(false);
        }
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [payRunId]);

  return {
    run, lines, payrollSettings, priorRuns, comparison, schedule,
    loading, error, isFinalized, setLines,
  };
}